/**
 * Item definitions. Data only; the server owns all inventory mutations.
 * Ids are stable strings (persisted in SQLite); never rename an id once shipped.
 *
 * Phase 2c rewrote the table for the MMORPG pivot. Guns, mods, ammo and the four gun lines are
 * gone: what is left is six melee/caster **stat sticks** (three per playable Calling), six visible
 * armour pieces per line across three tiers, and the materials economy, which never changed.
 *
 * ## Looks are 1-based, and 0 means bare
 *
 * `PlayerSnap.look` (`docs/PROTOCOL-V3.md` §4) reserves 0 for "bare" / "unarmed" in every gear
 * field, and the crowd bake (`tools/assetgen/src/bake-anim.ts`, `GEAR_LOOKS`) numbers its meshes
 * from 1. So a look id here is 1..3 for head/shoulders/chest and 1..6 for a weapon, and there is
 * no look 0 item. The frozen weapon table, in bake order:
 *
 *   1 vanguard sword · 2 vanguard axe · 3 vanguard hammer
 *   4 warden lantern-staff · 5 warden censer mace · 6 warden wand
 *
 * ## Every item has an icon
 *
 * `ItemDef.icon` names a cell in `apps/client/public/assets/gen/icons/atlas.json` when the piece
 * has a rendered GLB (every weapon and every visible-armour look does, by construction). The
 * stats-only slots — hands, legs, feet — and the materials, valuables and consumables have no GLB
 * and therefore no atlas cell; their `icon` names a drawn glyph in `apps/client/src/ui/icons.tsx`
 * instead. `iconAtlasCell()` there is the one place that decides which of the two a name is, so
 * nothing else has to care.
 */
import { CallingId, type CallingIdValue } from './callings.js';
import type { PlayerModifiers } from './skills.js';
// Type-only, and one-way: `stats.ts` owns `GearStatLine` and deliberately imports nothing from
// here (see its header), so the adapter at the bottom of this file is what joins the two.
import type { GearStatLine } from './stats.js';

/**
 * Five tiers, WoW's ladder. The numeric values are frozen (they are written into the `items.rarity`
 * column and into every save already on disk); only the *names* moved in M6 — `Relic` became `Epic`
 * and `Hymn` became `Legendary`, because the owner asked for the ladder players already know.
 */
export const Rarity = { Common: 0, Uncommon: 1, Rare: 2, Epic: 3, Legendary: 4 } as const;
export type RarityLevel = (typeof Rarity)[keyof typeof Rarity];
export const RARITY_NAMES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'] as const;
/**
 * HUD/loot colours per rarity (hex). White / green / blue / purple / orange — the WoW ladder,
 * specified verbatim by the owner. Orange is otherwise off-limits in the Requiem palette; this
 * ramp is the single sanctioned exception, and only for rarity.
 */
export const RARITY_COLORS = [0xf2effa, 0x3ddc84, 0x4aa3ff, 0xb36bff, 0xff9f1c] as const;

export type ItemKind = 'weapon' | 'armor' | 'consumable' | 'material' | 'valuable' | 'key';

/**
 * The six armour slots. `shoulders` is new in Phase 2c and is the third *visible* slot; the order
 * here is paperdoll order (visible three first), not wire order — nothing on the wire iterates it.
 */
export const ARMOR_SLOTS = ['head', 'shoulders', 'chest', 'hands', 'legs', 'feet'] as const;
export type ArmorSlot = (typeof ARMOR_SLOTS)[number];

/** The three slots that change the body in the world. The rest are numbers on a sheet. */
export const VISIBLE_ARMOR_SLOTS = ['head', 'shoulders', 'chest'] as const;
export type VisibleArmorSlot = (typeof VISIBLE_ARMOR_SLOTS)[number];
export const isVisibleArmorSlot = (s: string): s is VisibleArmorSlot =>
  (VISIBLE_ARMOR_SLOTS as readonly string[]).includes(s);

export const ARMOR_SLOT_NAMES: Record<ArmorSlot, string> = {
  head: 'Head',
  shoulders: 'Shoulders',
  chest: 'Chest',
  hands: 'Hands',
  legs: 'Legs',
  feet: 'Feet',
};

/**
 * The ceiling on any one stack, anywhere in the game (owner, 2026-09-04). Materials and valuables
 * — Hymn Shards included, they are a material — all sit exactly here; consumables sit at or under
 * it; equipment (weapons, armour) never stacks at all. `packages/shared/test/items.test.ts`
 * asserts those rules over the whole `ITEMS` table so a new item cannot break them.
 */
export const MAX_STACK = 20;

/**
 * The stack size an item of `kind` is allowed to declare, or null for "anything from 1 to
 * MAX_STACK" (consumables, whose sizes are tuned per item). One function so the item table, the
 * test and any future generator all read the same rule.
 */
export function stackRuleFor(kind: ItemKind): number | null {
  switch (kind) {
    case 'material':
    case 'valuable':
      return MAX_STACK;
    case 'weapon':
    case 'armor':
      return 1;
    default:
      // consumable, key — bounded by MAX_STACK but not pinned to it.
      return null;
  }
}

/** True if this item may ever share a slot with another of the same id. Equipment never can. */
export const isStackable = (d: ItemDef | undefined): boolean => (d?.stack ?? 1) > 1;

export interface ItemDef {
  id: string;
  name: string;
  kind: ItemKind;
  rarity: RarityLevel;
  /** Vendor value in Wick per unit. */
  value: number;
  /** Max units per stack. Never above `MAX_STACK`; see `stackRuleFor`. */
  stack: number;
  desc: string;
  /**
   * Atlas cell id (`assets/gen/icons/atlas.json`) or, where the piece has no rendered GLB, a drawn
   * glyph name in `ui/icons.tsx`. Required on every item — see the module header.
   */
  icon: string;
  /**
   * Character level required to equip. Absent means 1. Enforced server-side in `handleEquip`;
   * the UI only greys the row.
   */
  levelReq?: number;
  /** For kind 'weapon': the numeric id in `weapons.ts`, which is also its `look`. */
  weaponId?: number;
  /**
   * Look id for a piece the world can see: 1..3 for a head/shoulders/chest armour piece, 1..6 for
   * a weapon. Absent on the stats-only slots and on everything that is not equipment.
   */
  look?: number;
  /** For kind 'armor'. */
  armorSlot?: ArmorSlot;
  /** For kind 'armor': flat damage removed per hit before hp (capped, see `armorReduction`). */
  armor?: number;
  /**
   * Attack power and spell power, the two numbers `computeStats` in `stats.ts` folds every
   * ability coefficient through. Armour carries them; a weapon's contribution is its damage.
   */
  ap?: number;
  sp?: number;
  /**
   * Calling restriction (`callings.CallingId`), or undefined for "anyone". Applies to armour and
   * to class weapons. The server refuses to equip a restricted piece on the wrong Calling.
   */
  calling?: CallingIdValue;
  /** For kind 'armor': the secondary modifier the piece folds into `PlayerModifiers`. */
  mods?: Partial<PlayerModifiers>;
  /** For kind 'consumable'. */
  effect?: { heal?: number; overMs?: number };
}

const def = (d: ItemDef): [string, ItemDef] => [d.id, d];

// ================================================================ durability
//
// Owner's rule, Phase 2c: every equipped piece loses 10 points on death; at 0 it gives no stats
// and its look renders greyed; repair costs `value x missing fraction x 0.1` Wick. The whole
// mechanic is these five numbers plus four pure functions, so the server, the vendor panel and
// the tooltip can never disagree about what a repair costs.

export const MAX_DURABILITY = 100;
/** Points lost by every equipped piece when the wearer dies. */
export const DEATH_DURABILITY_LOSS = 10;
/** Wick per point of `ItemDef.value` per unit of missing durability fraction. */
export const REPAIR_WICK_RATE = 0.1;

/** Weapons and armour wear. Nothing else does — a stim does not rust. */
export const isDamageable = (d: ItemDef | undefined): boolean =>
  d?.kind === 'weapon' || d?.kind === 'armor';

/**
 * Durability of a stack, 0..`MAX_DURABILITY`. Absent means pristine, which is what every row
 * written before the `durability` column existed reads as — see the 0006 migration's default.
 */
export function durabilityOf(stack: Pick<ItemStack, 'itemId' | 'durability'>): number {
  if (!isDamageable(ITEMS[stack.itemId])) return MAX_DURABILITY;
  const d = stack.durability;
  if (d === undefined || !Number.isFinite(d)) return MAX_DURABILITY;
  return Math.max(0, Math.min(MAX_DURABILITY, Math.round(d)));
}

/** A broken piece: still worn, still visible, worth nothing. */
export const isBroken = (stack: Pick<ItemStack, 'itemId' | 'durability'>): boolean =>
  isDamageable(ITEMS[stack.itemId]) && durabilityOf(stack) <= 0;

/**
 * Wick to bring one stack back to full. Zero when it is already full or cannot wear; never below
 * 1 Wick for a piece that *is* damaged, so a 6-Wick glove is not repaired for nothing.
 */
export function repairCost(stack: Pick<ItemStack, 'itemId' | 'durability'>): number {
  const d = ITEMS[stack.itemId];
  if (!d || !isDamageable(d)) return 0;
  const missing = (MAX_DURABILITY - durabilityOf(stack)) / MAX_DURABILITY;
  if (missing <= 0) return 0;
  return Math.max(1, Math.round(d.value * missing * REPAIR_WICK_RATE));
}

/** Wick to repair everything in `stacks` that is damaged. */
export const repairAllCost = (stacks: readonly ItemStack[]): number =>
  stacks.reduce((n, s) => n + repairCost(s), 0);

// ---------------------------------------------------------------- armour generation
//
// Hand-writing forty-two pieces would be forty-two chances to fat-finger a number, so the set is
// generated from three small tables: how much a slot is worth, what a tier is worth, and which
// look a tier wears. Ids are deterministic (`arm_<line>_<slot>_<rarity name>`) and therefore
// stable — treat them as hand-written from here.

/** Relative armour weight per slot: a chest plate is worth more than a glove. */
const SLOT_ARMOR_WEIGHT: Record<ArmorSlot, number> = {
  head: 0.6,
  shoulders: 0.5,
  chest: 1,
  hands: 0.4,
  legs: 0.7,
  feet: 0.4,
};
/** Base flat reduction at each rarity, before the slot weight. */
const RARITY_ARMOR_BASE: Record<RarityLevel, number> = { 0: 1, 1: 2, 2: 3, 3: 5, 4: 7 };
/**
 * Attack / spell power at each rarity, before the slot weight. This is the number that makes a
 * Rare set feel like a Rare set: `computeStats` multiplies every ability coefficient by it, so a
 * full Epic set roughly doubles a bare level-9's damage. A line hands its whole budget to the
 * stat its Calling actually uses — plate carries attack power, weave carries spell power — and
 * the open Waker line splits it, because it is worn before a Calling is picked.
 */
const RARITY_POWER_BASE: Record<RarityLevel, number> = { 0: 2, 1: 5, 2: 9, 3: 15, 4: 22 };
/** Vendor value at each rarity, before the slot weight. */
const RARITY_ARMOR_VALUE: Record<RarityLevel, number> = { 0: 60, 1: 140, 2: 320, 3: 760, 4: 1800 };

/**
 * One secondary stat per slot, scaled by rarity — so a full set spreads across six different
 * numbers rather than stacking one six times, and each tier up is a visible jump. Common gets
 * nothing but armour: white items are meant to feel like the floor.
 *
 * The gun-era secondaries (reload time, mod power, sprint cost) went with the guns; what is left
 * is what a tab-target fight actually reads: health, stagger, damage, speed, stamina, cooldowns.
 */
const SECONDARY_SCALE: Record<RarityLevel, number> = { 0: 0, 1: 1, 2: 1.7, 3: 2.7, 4: 4 };
const SLOT_SECONDARY: Record<
  ArmorSlot,
  { label: string; at1: number; apply: (v: number) => Partial<PlayerModifiers> }
> = {
  head: { label: 'cooldowns', at1: 0.02, apply: (v) => ({ cooldownMult: v }) },
  shoulders: { label: 'stagger resist', at1: 0.04, apply: (v) => ({ staggerResist: v }) },
  chest: { label: 'max HP', at1: 6, apply: (v) => ({ maxHp: Math.round(v) }) },
  hands: { label: 'damage', at1: 0.02, apply: (v) => ({ damageMult: v }) },
  legs: { label: 'move speed', at1: 0.01, apply: (v) => ({ speedMult: v }) },
  feet: { label: 'max stamina', at1: 6, apply: (v) => ({ maxStamina: Math.round(v) }) },
};

/**
 * The look worn at each rung of a line, per visible slot. Three tiers, three meshes; the Epic rung
 * reuses the Rare silhouette on purpose — Epic is the same armour, better made, and re-baking a
 * fourth mesh per slot is not what the top of a ten-level ladder needs.
 */
const TIER_LOOK = [1, 2, 3, 3] as const;

/** Armour lines: one open starter line, one per playable Calling for the rungs above it. */
interface ArmorRung {
  rarity: RarityLevel;
  level: number;
  /** Index into `TIER_LOOK`. */
  tier: 0 | 1 | 2 | 3;
}
interface ArmorLine {
  key: string;
  name: string;
  calling?: CallingIdValue;
  rungs: ArmorRung[];
  flavour: string;
  /** Share of the line's power budget that lands on attack power; the rest is spell power. */
  apShare: number;
}
const ARMOR_LINES: ArmorLine[] = [
  {
    key: 'waker',
    name: 'Waker',
    rungs: [{ rarity: Rarity.Common, level: 1, tier: 0 }],
    flavour: 'Hall-issue. It was never meant to stop anything.',
    apShare: 0.5,
  },
  {
    key: 'bulwark',
    name: 'Bulwark',
    calling: CallingId.Vanguard,
    rungs: [
      { rarity: Rarity.Uncommon, level: 3, tier: 1 },
      { rarity: Rarity.Rare, level: 6, tier: 2 },
      { rarity: Rarity.Epic, level: 9, tier: 3 },
    ],
    flavour: 'Bell-bronze plate, cut for a body that intends to be hit.',
    apShare: 1,
  },
  {
    key: 'vigil',
    name: 'Vigil',
    calling: CallingId.Warden,
    rungs: [
      { rarity: Rarity.Uncommon, level: 3, tier: 1 },
      { rarity: Rarity.Rare, level: 6, tier: 2 },
      { rarity: Rarity.Epic, level: 9, tier: 3 },
    ],
    flavour: 'Warden weave. Every seam is a place to hang a lantern.',
    apShare: 0,
  },
];

const PIECE_NAME: Record<ArmorSlot, string> = {
  head: 'Hood',
  shoulders: 'Mantle',
  chest: 'Plate',
  hands: 'Gloves',
  legs: 'Greaves',
  feet: 'Boots',
};

/**
 * Atlas cell per visible slot per look id. These are the rendered GLBs' own ids, so a piece's icon
 * is a picture of the exact mesh the world puts on the body — the plan's "every look has its own
 * icon by construction", made literal.
 */
export const LOOK_ICONS: Record<VisibleArmorSlot, readonly [string, string, string]> = {
  head: ['head-hood', 'head-kettle-helm', 'head-great-helm'],
  shoulders: ['shoulder-leather-pads.l', 'shoulder-plate-pauldrons.l', 'shoulder-lantern-mantle.l'],
  chest: ['chest-tabard', 'chest-brigandine', 'chest-cuirass'],
};

/** Drawn-glyph fallbacks for the three slots that have no GLB and therefore no atlas cell. */
const STATS_ONLY_ICONS: Record<'hands' | 'legs' | 'feet', string> = {
  hands: 'armor-hands',
  legs: 'armor-legs',
  feet: 'armor-feet',
};

/** The icon for an armour piece: its own look's cell, or the slot's drawn fallback. */
export function armorIconFor(slot: ArmorSlot, look: number | undefined): string {
  if (isVisibleArmorSlot(slot)) {
    const cells = LOOK_ICONS[slot];
    return cells[Math.max(0, Math.min(cells.length - 1, (look ?? 1) - 1))] ?? cells[0];
  }
  return STATS_ONLY_ICONS[slot as 'hands' | 'legs' | 'feet'];
}

function armorItems(): [string, ItemDef][] {
  const out: [string, ItemDef][] = [];
  for (const line of ARMOR_LINES) {
    for (const rung of line.rungs) {
      for (const slot of ARMOR_SLOTS) {
        const weight = SLOT_ARMOR_WEIGHT[slot];
        const sec = SLOT_SECONDARY[slot];
        const scaled = sec.at1 * SECONDARY_SCALE[rung.rarity];
        const mods =
          SECONDARY_SCALE[rung.rarity] === 0
            ? undefined
            : sec.apply(Math.round(scaled * 1000) / 1000);
        const look = isVisibleArmorSlot(slot) ? TIER_LOOK[rung.tier] : undefined;
        const power = RARITY_POWER_BASE[rung.rarity] * weight;
        const ap = Math.round(power * line.apShare);
        const sp = Math.round(power * (1 - line.apShare));
        const d: ItemDef = {
          id: `arm_${line.key}_${slot}_${RARITY_NAMES[rung.rarity].toLowerCase()}`,
          name: `${line.name} ${PIECE_NAME[slot]}`,
          kind: 'armor',
          rarity: rung.rarity,
          value: Math.max(10, Math.round(RARITY_ARMOR_VALUE[rung.rarity] * weight)),
          stack: 1,
          desc: line.flavour,
          icon: armorIconFor(slot, look),
          armorSlot: slot,
          armor: Math.max(1, Math.round(RARITY_ARMOR_BASE[rung.rarity] * weight)),
          ...(ap > 0 ? { ap } : {}),
          ...(sp > 0 ? { sp } : {}),
          ...(rung.level > 1 ? { levelReq: rung.level } : {}),
          ...(look === undefined ? {} : { look }),
          ...(line.calling === undefined ? {} : { calling: line.calling }),
          ...(mods === undefined ? {} : { mods }),
        };
        out.push(def(d));
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- class weapons
//
// Three per Calling, and each one *is* a look: `weaponId === look`, in the order the crowd bake
// froze (see the module header). The stats live in `weapons.ts` and are built from this same
// table, so the item shell and the `WeaponDef` cannot drift — both read `CLASS_WEAPONS`.

export interface ClassWeaponDef {
  id: string;
  name: string;
  calling: CallingIdValue;
  /** 1..6. Also the `weaponId` and the atlas cell's index in `WEAPON_LOOK_ICONS`. */
  look: number;
  rarity: RarityLevel;
  levelReq: number;
  value: number;
  /** Damage per swing, before any modifier: inclusive range, server rolls uniformly inside it. */
  damage: [min: number, max: number];
  /** Milliseconds between auto-attack swings. */
  speedMs: number;
  /** Reach in metres. A staff outranges a maul. */
  range: number;
  desc: string;
}

/** Atlas cell per weapon look, 1..6 — the crowd bake's own file names. */
export const WEAPON_LOOK_ICONS: readonly string[] = [
  'weapon-vanguard-sword',
  'weapon-vanguard-axe',
  'weapon-vanguard-hammer',
  'weapon-warden-lantern-staff',
  'weapon-warden-censer-mace',
  'weapon-warden-wand',
];

/**
 * Six stat sticks. Vanguard swings faster and shorter; the Warden's are slower, longer and hit
 * harder per swing, because a Warden's damage is meant to come off the bar and not off the stick.
 *
 * The third Warden rung is the hymnal-and-wand pair. Only the wand is in the crowd bake's parts
 * list (`weapon-warden-hymnal.glb` exists but no look references it), so the body carries the wand
 * and the hymnal is a name, not a mesh, until somebody re-bakes.
 */
export const CLASS_WEAPONS: readonly ClassWeaponDef[] = [
  {
    id: 'wpn_compact_blade',
    name: 'Compact Blade',
    calling: CallingId.Vanguard,
    look: 1,
    rarity: Rarity.Common,
    levelReq: 1,
    value: 90,
    damage: [7, 11],
    speedMs: 2000,
    range: 3,
    desc: 'Hall-issue, stamped at the crossguard. Every Vanguard has carried one and none has kept one.',
  },
  {
    id: 'wpn_tithe_cleaver',
    name: 'Tithe Cleaver',
    calling: CallingId.Vanguard,
    look: 2,
    rarity: Rarity.Uncommon,
    levelReq: 4,
    value: 260,
    damage: [13, 21],
    speedMs: 2400,
    range: 3,
    desc: 'The Barrows were opened with one of these, a lid at a time. It has not lost the habit.',
  },
  {
    id: 'wpn_sextons_maul',
    name: "Sexton's Maul",
    calling: CallingId.Vanguard,
    look: 3,
    rarity: Rarity.Rare,
    levelReq: 8,
    value: 720,
    damage: [24, 38],
    speedMs: 3000,
    range: 3,
    desc: 'Taken off a Sexton and re-hafted. It still swings like it is digging.',
  },
  {
    id: 'wpn_long_vigil',
    name: 'The Long Vigil',
    calling: CallingId.Warden,
    look: 4,
    rarity: Rarity.Common,
    levelReq: 1,
    value: 90,
    damage: [6, 12],
    speedMs: 2200,
    range: 4,
    desc: 'A staff with a lamp on it. The lamp is the point; the staff is what you lean on at dawn.',
  },
  {
    id: 'wpn_ashfall_censer',
    name: 'Ashfall Censer',
    calling: CallingId.Warden,
    look: 5,
    rarity: Rarity.Uncommon,
    levelReq: 4,
    value: 260,
    damage: [12, 22],
    speedMs: 2500,
    range: 3.5,
    desc: 'Swung on a chain, it smokes. Swung at a Chorister, it stops smoking.',
  },
  {
    id: 'wpn_wick_and_hymnal',
    name: 'Wick and Hymnal',
    calling: CallingId.Warden,
    look: 6,
    rarity: Rarity.Rare,
    levelReq: 8,
    value: 720,
    damage: [22, 36],
    speedMs: 2800,
    range: 5,
    desc: 'A wand in one hand and the book it argues with in the other. The book usually wins.',
  },
];

function classWeaponItems(): [string, ItemDef][] {
  return CLASS_WEAPONS.map((w) =>
    def({
      id: w.id,
      name: w.name,
      kind: 'weapon',
      rarity: w.rarity,
      value: w.value,
      stack: 1,
      weaponId: w.look,
      look: w.look,
      levelReq: w.levelReq,
      calling: w.calling,
      icon: WEAPON_LOOK_ICONS[w.look - 1] ?? 'sword-1h',
      desc: w.desc,
    }),
  );
}

export const ITEMS: Record<string, ItemDef> = Object.fromEntries([
  // Consumables
  def({
    id: 'med_stim',
    name: 'Tallow Stim',
    kind: 'consumable',
    rarity: Rarity.Common,
    value: 25,
    stack: 5,
    icon: 'consumable-stim',
    effect: { heal: 40, overMs: 4000 },
    desc: 'Burns going in. Heals 40 over four seconds.',
  }),
  def({
    id: 'med_kit',
    name: 'Warden Kit',
    kind: 'consumable',
    rarity: Rarity.Uncommon,
    value: 90,
    stack: 2,
    icon: 'consumable-kit',
    effect: { heal: 100, overMs: 8000 },
    desc: 'Full field dressing. Slow; find cover.',
  }),
  // Materials — every one stacks to MAX_STACK (owner, 2026-09-04: "materials and valuables should
  // be stackable up to qty 20. same with hymn shards").
  def({
    id: 'scrap_hull',
    name: 'Bell-Bronze Scrap',
    kind: 'material',
    rarity: Rarity.Common,
    value: 8,
    stack: MAX_STACK,
    icon: 'material-scrap',
    desc: 'Cast from the bells that came down with the tower. The Compact still pays by weight.',
  }),
  def({
    id: 'wire_bundle',
    name: 'Chime Wire',
    kind: 'material',
    rarity: Rarity.Common,
    value: 12,
    stack: MAX_STACK,
    icon: 'material-wire',
    desc: 'Coiled bell-wire, pulled from the rubble. Somehow it still hums.',
  }),
  def({
    id: 'ember_glass',
    name: 'Ember Glass',
    kind: 'material',
    rarity: Rarity.Uncommon,
    value: 35,
    stack: MAX_STACK,
    icon: 'material-glass',
    desc: 'Sunward sand, fused hard by the Stillsun. Lantern glass, wardglass, trade.',
  }),
  def({
    id: 'hymn_shard',
    name: 'Hymn Shard',
    kind: 'material',
    rarity: Rarity.Rare,
    value: 120,
    stack: MAX_STACK,
    icon: 'material-shard',
    desc: 'A fragment of a Hymn. It hums if you hold it long enough.',
  }),
  def({
    id: 'choir_core',
    name: 'Choir Core',
    kind: 'material',
    rarity: Rarity.Legendary,
    value: 900,
    stack: MAX_STACK,
    icon: 'material-core',
    desc: 'The mind of a Hymn. The Hymnal will pay anything. So will the Ferry.',
  }),
  // Valuables — same rule, same cap.
  def({
    id: 'colonist_tag',
    name: "Congregant's Tag",
    kind: 'valuable',
    rarity: Rarity.Uncommon,
    value: 45,
    stack: MAX_STACK,
    icon: 'valuable-tag',
    desc: 'A name, a pew number, a date three centuries gone.',
  }),
  def({
    id: 'data_slate',
    name: 'Hymnal Slate',
    kind: 'valuable',
    rarity: Rarity.Rare,
    value: 160,
    stack: MAX_STACK,
    icon: 'valuable-slate',
    desc: 'Old choir sheets, slate and wax. Some still hum the tune.',
  }),
  def({
    id: 'candle_relic',
    name: 'Cathedral Relic',
    kind: 'valuable',
    rarity: Rarity.Epic,
    value: 480,
    stack: MAX_STACK,
    icon: 'valuable-relic',
    desc: 'Something from the bell tower, where nothing should have lasted this long.',
  }),
  // Class weapons — six stat sticks (see `CLASS_WEAPONS`); combat numbers live in weapons.ts.
  ...classWeaponItems(),
  // Armour — 42 generated pieces (see `armorItems`).
  ...armorItems(),
]);

/** Human-readable label for an armour piece's secondary stat, for the paperdoll's tooltip. */
export function secondaryLabel(slot: ArmorSlot): string {
  return SLOT_SECONDARY[slot].label;
}

// ---------------------------------------------------------------- armour maths

/**
 * How much of one incoming hit a set of armour removes. Flat, so armour matters most against the
 * Choir's chip damage and least against a Sexton's slam — but capped twice so a full Epic set
 * can never make a Waker immune: never more than `ARMOR_MAX_FLAT` absolute, and never more than
 * `ARMOR_MAX_FRACTION` of the hit. At least 1 damage always lands.
 */
export const ARMOR_MAX_FLAT = 20;
export const ARMOR_MAX_FRACTION = 0.5;

export function armorReduction(totalArmor: number, amount: number): number {
  if (totalArmor <= 0 || amount <= 0) return 0;
  const capped = Math.min(totalArmor, ARMOR_MAX_FLAT, amount * ARMOR_MAX_FRACTION);
  return Math.min(Math.max(0, amount - 1), Math.floor(capped));
}

/** True if `calling` (0 = none picked) may equip `def`. */
export function callingCanUse(def: ItemDef | undefined, calling: number): boolean {
  if (!def) return false;
  if (def.calling === undefined) return true;
  return def.calling === calling;
}

/** True if a character at `level` meets `def`'s level requirement. */
export const levelCanUse = (def: ItemDef | undefined, level: number): boolean =>
  def !== undefined && level >= (def.levelReq ?? 1);

/** Where an item stack lives. Slot index applies to Quick (0-2) and Backpack/Stash ordering. */
export const ItemLocation = {
  Stash: 0,
  Backpack: 1,
  Satchel: 2,
  /** The one weapon slot. Kept at 3 (`loadout:primary` in SQLite) so no row has to migrate. */
  Primary: 3,
  Secondary: 4,
  Quick: 5,
  Ground: 6,
  // Equipped armour is an ordinary item row in a one-capacity location, so it inherits ownership,
  // insurance, death/disconnect and persistence handling for free.
  Head: 7,
  Chest: 8,
  Legs: 9,
  Feet: 10,
  Hands: 11,
  /** Phase 2c: the third visible slot. `equip:shoulders` in SQLite. */
  Shoulders: 12,
} as const;
export type ItemLocationKind = (typeof ItemLocation)[keyof typeof ItemLocation];

/** The weapon is worn in `Primary`; this alias is what the paperdoll and the tooltips read. */
export const WEAPON_LOCATION = ItemLocation.Primary;

export const ARMOR_SLOT_LOCATION: Record<ArmorSlot, ItemLocationKind> = {
  head: ItemLocation.Head,
  shoulders: ItemLocation.Shoulders,
  chest: ItemLocation.Chest,
  hands: ItemLocation.Hands,
  legs: ItemLocation.Legs,
  feet: ItemLocation.Feet,
};
export const LOCATION_ARMOR_SLOT: Partial<Record<ItemLocationKind, ArmorSlot>> = {
  [ItemLocation.Head]: 'head',
  [ItemLocation.Shoulders]: 'shoulders',
  [ItemLocation.Chest]: 'chest',
  [ItemLocation.Hands]: 'hands',
  [ItemLocation.Legs]: 'legs',
  [ItemLocation.Feet]: 'feet',
};

/**
 * Every location that counts as "worn". An explicit set, not a range: `Shoulders` (12) sits above
 * the old 7..11 block and the weapon slot (3) sits below it, so the range check this replaced was
 * wrong in both directions.
 */
export const EQUIP_LOCATIONS: readonly ItemLocationKind[] = [
  ItemLocation.Primary,
  ItemLocation.Head,
  ItemLocation.Shoulders,
  ItemLocation.Chest,
  ItemLocation.Hands,
  ItemLocation.Legs,
  ItemLocation.Feet,
];
const EQUIP_LOCATION_SET: ReadonlySet<number> = new Set<number>(EQUIP_LOCATIONS);
export const isEquipLocation = (l: number): boolean => EQUIP_LOCATION_SET.has(l);

/** The location an item is worn in, or null if it is not equipment. */
export function equipLocationFor(d: ItemDef | undefined): ItemLocationKind | null {
  if (!d) return null;
  if (d.kind === 'weapon') return WEAPON_LOCATION;
  if (d.kind === 'armor' && d.armorSlot !== undefined) return ARMOR_SLOT_LOCATION[d.armorSlot];
  return null;
}

export interface ItemStack {
  /** Server-assigned unique id for this stack. */
  uid: number;
  itemId: string;
  qty: number;
  location: ItemLocationKind;
  /** Slot index within the location (Quick 0-2, Backpack 0-11, Satchel 0-1, Stash 0-39). */
  slot: number;
  insured: boolean;
  /**
   * 0..100 for weapons and armour; absent (and meaningless) for everything else. Read it through
   * `durabilityOf`, never directly — absent means pristine, and the wire always sends a number.
   */
  durability?: number;
}

export const BACKPACK_SLOTS = 12;
export const SATCHEL_SLOTS = 2;
export const STASH_SLOTS = 40;
export const QUICK_SLOTS = 3;
/** Insurance premium as a fraction of item value. */
export const INSURANCE_RATE = 0.15;

export function itemValue(stack: ItemStack): number {
  const d = ITEMS[stack.itemId];
  return d ? d.value * stack.qty : 0;
}

/**
 * Sum the armour value and the secondary modifiers of everything in the equipment slots.
 *
 * **A broken piece contributes nothing.** Durability is not a debuff on the number, it is the
 * number being switched off — see `equippedFor`, which enforces the same rule for `computeStats`.
 */
export function equippedArmor(stacks: readonly ItemStack[]): {
  armor: number;
  mods: Partial<PlayerModifiers>;
  bySlot: Partial<Record<ArmorSlot, ItemStack>>;
} {
  let armor = 0;
  const mods: Record<string, number> = {};
  const bySlot: Partial<Record<ArmorSlot, ItemStack>> = {};
  for (const s of stacks) {
    const slot = LOCATION_ARMOR_SLOT[s.location];
    if (slot === undefined) continue;
    const d = ITEMS[s.itemId];
    if (!d || d.kind !== 'armor') continue;
    bySlot[slot] = s;
    if (isBroken(s)) continue;
    armor += d.armor ?? 0;
    for (const [k, v] of Object.entries(d.mods ?? {})) {
      if (typeof v === 'number') mods[k] = (mods[k] ?? 0) + v;
    }
  }
  return { armor, mods: mods as Partial<PlayerModifiers>, bySlot };
}

/** The stack in each equipment location, weapon included. Broken pieces are still *worn*. */
export function equippedStacks(stacks: readonly ItemStack[]): {
  weapon: ItemStack | null;
  bySlot: Partial<Record<ArmorSlot, ItemStack>>;
} {
  let weapon: ItemStack | null = null;
  const bySlot: Partial<Record<ArmorSlot, ItemStack>> = {};
  for (const s of stacks) {
    if (s.location === WEAPON_LOCATION) {
      if (ITEMS[s.itemId]?.kind === 'weapon') weapon = s;
      continue;
    }
    const slot = LOCATION_ARMOR_SLOT[s.location];
    if (slot === undefined) continue;
    if (ITEMS[s.itemId]?.kind === 'armor') bySlot[slot] = s;
  }
  return { weapon, bySlot };
}

/**
 * **The contract with `computeStats(level, calling, equipped[])`.**
 *
 * Every `ItemDef` the character is currently *getting value from*: the weapon plus the six armour
 * slots, in `ARMOR_SLOTS` order with the weapon last. **Pieces at 0 durability are excluded** —
 * that is what "gives no stats" means, and it is enforced here rather than at each caller so the
 * combat side never has to know durability exists.
 *
 * Pure over `readonly ItemStack[]`, so the server, the hub's paperdoll and a test can all call it.
 */
export function equippedFor(stacks: readonly ItemStack[]): ItemDef[] {
  const { weapon, bySlot } = equippedStacks(stacks);
  const out: ItemDef[] = [];
  for (const slot of ARMOR_SLOTS) {
    const s = bySlot[slot];
    if (!s || isBroken(s)) continue;
    const d = ITEMS[s.itemId];
    if (d) out.push(d);
  }
  if (weapon && !isBroken(weapon)) {
    const d = ITEMS[weapon.itemId];
    if (d) out.push(d);
  }
  return out;
}

/**
 * `ItemDef` -> `GearStatLine` (`stats.ts`), the shape `computeStats(level, calling, equipped[])`
 * reads.
 *
 * It is a translation and not a cast, because the two vocabularies genuinely differ: an `ItemDef`
 * spells armour `armor` and carries a weapon's damage as an inclusive **range** in `weapons.ts`,
 * while the stat fold wants `armour` and a single number. `stats.ts` deliberately does not import
 * this module (see its header), so the adapter lives on this side of the seam.
 *
 * `weaponStats` is injected for the same reason: `weapons.ts` imports this file, so this file
 * cannot import it back. `equippedGearStats` there is the bound version and the one to call.
 */
export function statLineFor(
  d: ItemDef,
  weaponStats?: (weaponId: number) => { damage: [number, number]; speedMs: number } | undefined,
): GearStatLine {
  const line: GearStatLine = {};
  if (d.ap) line.ap = d.ap;
  if (d.sp) line.sp = d.sp;
  if (d.armor) line.armour = d.armor;
  if (d.mods?.maxHp) line.maxHp = d.mods.maxHp;
  if (d.weaponId !== undefined && weaponStats) {
    const w = weaponStats(d.weaponId);
    if (w) {
      line.damage = (w.damage[0] + w.damage[1]) / 2;
      line.speedMs = w.speedMs;
    }
  }
  return line;
}

/**
 * **The contract with the combat side, in the shape it actually wants.** `equippedFor` gives the
 * definitions; this gives the numbers, already filtered by durability, ready to hand straight to
 * `computeStats`. Pass `weaponStats` as `(id) => WEAPONS[id]` from wherever `weapons.ts` is in
 * scope — this module cannot import it without a cycle.
 */
export function equippedStatLines(
  stacks: readonly ItemStack[],
  weaponStats?: (weaponId: number) => { damage: [number, number]; speedMs: number } | undefined,
): GearStatLine[] {
  return equippedFor(stacks).map((d) => statLineFor(d, weaponStats));
}

/**
 * The four visible look ids the equipped set produces, for `packLook`. Broken pieces are still
 * drawn — you are wearing a ruined helm, not no helm — the client greys them (`SnapFlags.Broken`).
 */
export function equippedLooks(stacks: readonly ItemStack[]): {
  head: number;
  shoulders: number;
  chest: number;
  weapon: number;
} {
  const { weapon, bySlot } = equippedStacks(stacks);
  const lookOf = (s: ItemStack | undefined | null): number =>
    s ? (ITEMS[s.itemId]?.look ?? 0) : 0;
  return {
    head: lookOf(bySlot.head),
    shoulders: lookOf(bySlot.shoulders),
    chest: lookOf(bySlot.chest),
    weapon: lookOf(weapon),
  };
}

/** True if anything worn is at 0 durability — drives `SnapFlags.Broken` and the greyed body. */
export function hasBrokenEquipped(stacks: readonly ItemStack[]): boolean {
  for (const s of stacks) {
    if (!isEquipLocation(s.location)) continue;
    if (isBroken(s)) return true;
  }
  return false;
}

/**
 * The rarity ladder as a packed 16-bit cosmetics word: three bits per slot, in `COSMETIC_SLOTS`
 * order, holding `rarity + 1` (0 = that slot is empty). Rides on the Welcome roster and
 * PlayerJoined — once per player, not 30 times a second — so the hub's roster tiles can tint.
 *
 * `COSMETIC_SLOTS` is five long and **frozen**, deliberately not `ARMOR_SLOTS`: five slots x 3
 * bits is exactly the u16 the wire carries, and iterating a six-slot list here would have silently
 * truncated the sixth. Live appearance is `PlayerSnap.look` (§4), which has room for all of it.
 */
export const COSMETIC_SLOTS = ['head', 'chest', 'legs', 'feet', 'hands'] as const;

export function packCosmetics(bySlot: Partial<Record<ArmorSlot, ItemStack>>): number {
  let word = 0;
  COSMETIC_SLOTS.forEach((slot, i) => {
    const stack = bySlot[slot];
    const d = stack ? ITEMS[stack.itemId] : undefined;
    if (d && d.kind === 'armor') word |= ((d.rarity + 1) & 7) << (i * 3);
  });
  return word & 0xffff;
}

/** Inverse of `packCosmetics`: rarity per slot, or -1 for an empty slot. */
export function unpackCosmetics(word: number): Record<(typeof COSMETIC_SLOTS)[number], number> {
  const out = {} as Record<(typeof COSMETIC_SLOTS)[number], number>;
  COSMETIC_SLOTS.forEach((slot, i) => {
    out[slot] = ((word >> (i * 3)) & 7) - 1;
  });
  return out;
}
